import * as React from 'react';
import {
  Box,
  IconButton,

} from '@mui/material';
// import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { RichTreeView } from '@mui/x-tree-view';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import { TreeItem, TreeItemContent } from '@mui/x-tree-view/TreeItem';
import {useTreeItem} from '@mui/x-tree-view/useTreeItem'

import AuthModal from '../../modals/AuthModal';

const OracleEPMContext = React.createContext(new Set());

const CustomContent = React.forwardRef(function CustomContent({ children, toggleItemDisabled, itemId, ...props }, ref) {
  const isTopLevel = itemId?.includes('#');
  const strList = itemId.split('#');
  let disabled = strList[1] === 'False' ? true : false;
  let sourceName = strList[0];
  const [openAuth, setOpenAuth] = React.useState(false);

  return (
    <>
      <TreeItemContent {...props} ref={ref}>
        {children}
        {isTopLevel && (
          <IconButton size="small" onClick={() => setOpenAuth(true)} disabled={!disabled} sx={{ marginLeft: 'auto' }}>
            {disabled ? <LockOutlinedIcon fontSize="small" /> : <LockOpenOutlinedIcon fontSize="small" />}
          </IconButton>
        )}
      </TreeItemContent>
      <AuthModal open={openAuth} sourceName={sourceName} handleClose={() => setOpenAuth(false)} handleSave={(u, p) => { console.log('Saving credentials for:', sourceName, u, p); setOpenAuth(false); }} />
    </>
  );
});

const CustomTreeItem = React.forwardRef(function CustomTreeItem(props, ref) {
  const { publicAPI, status } = useTreeItem(props);

  const toggleItemDisabled = () =>
    publicAPI.setIsItemDisabled({
      itemId: props.itemId,
      isItemDisabled: !status.disabled,
    });

  const oracleEPMSources = React.useContext(OracleEPMContext);
  const itemId = props.itemId || '';
  const caretCount = (itemId.match(/\^/g) || []).length;
  const isTopLevel = itemId.includes('#');
  const sourceName = itemId.split(/[#^]/)[0];
  const isOracleEPMCube = caretCount === 1 && oracleEPMSources.has(sourceName);

  let itemIcon;
  if (isTopLevel) {
    itemIcon = <FolderIcon sx={{ fontSize: '0.9rem', color: '#f59e0b', flexShrink: 0 }} />;
  } else if (isOracleEPMCube) {
    itemIcon = <ViewInArIcon sx={{ fontSize: '0.9rem', color: '#7c3aed', flexShrink: 0 }} />;
  } else {
    itemIcon = <DescriptionIcon sx={{ fontSize: '0.9rem', color: '#64748b', flexShrink: 0 }} />;
  }

  const LabelWithIcon = ({ children }) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
      {itemIcon}
      <span>{children}</span>
    </Box>
  );

  return (
    <TreeItem
      {...props}
      ref={ref}
      slots={{ content: CustomContent, label: LabelWithIcon }}
      slotProps={{
        content: {
          toggleItemDisabled,
          disabled: status.disabled,
          itemId: props.itemId,
        },
      }}
    />
  );
});

export default function SourceTree(props) {
  const { data, onSelectSession } = props;
  const [treeItems, setTreeItems] = React.useState([])

  const renderTreeItems = (items, root) => (  
    items.map(item => (  
      <TreeItem key={item.name} itemId={`${root}^${item.name}`} label={item.name}>  
        {item.items && item.items.length > 0 && renderTreeItems(item.items, `${root}^${item.name}`)}  
      </TreeItem>  
    ))  
  );

  React.useEffect(() => {
    const mapTreeItems = mapDataToMUIXProducts(data);
    setTreeItems(mapTreeItems)
  }, data)

  const getCredentialStatus = (id) => {
    const treeItem = data.filter(item => item.name === id);
    if (treeItem.length === 0) return { is_admin_credential: true };
    
    const { is_admin_credential } = treeItem[0];
    return { 
      is_admin_credential: is_admin_credential || false
    };
  }

  const mapDataToMUIXProducts = (data) => {
    return data.map((source) => {
      let lockStatus = source.is_admin_credential === false ? 'False' : 'True';
      const isOracleEPM = source.type === 'oracleepm';

      if (isOracleEPM) {
        return {
          id: `${source.name}#${lockStatus}`,
          label: source.name,
          children: (source.items || []).map((cube) => ({
            id: `${source.name}^${cube.name}`,
            label: cube.name,
            children: (cube.items || []).map((session) => ({
              id: `${source.name}^${cube.name}^${session.name}`,
              label: session.name,
            })),
          })),
        };
      } else {
        return {
          id: `${source.name}#${lockStatus}`,
          label: source.name,
          children: (source.items || []).map((item) => ({
            id: `${source.name}^${item.name}`,
            label: item.name,
          })),
        };
      }
    });
  }

  const getSourceType = (sourceName) => {
    const treeItem = data.filter(item => item.name === sourceName);
    if (treeItem.length === 0) return null;
    return treeItem[0].type;
  }

  const handleItem = (e, itemId) => {
    if (itemId.includes('#'))
    {
      // Source level clicked
      let strList = itemId.split('#');
      let sourceName = strList[0];
      let sourceType = getSourceType(sourceName);
      let credentialInfo = getCredentialStatus(sourceName);
      
      onSelectSession(sourceName, sourceType, '', null, credentialInfo.is_admin_credential);
    }
    else {
      let strList = itemId.split('^');
      let sourceName = strList[0];
      let sourceType = getSourceType(sourceName);
      
      // Check if this is Oracle EPM (3-level: source^cube^session)
      if (sourceType === 'oracleepm' && strList.length === 3) {
        // Oracle EPM session clicked: source^cube^session
        let cubeName = strList[1];
        let sessionName = strList[2];
        let credentialInfo = getCredentialStatus(sourceName);
        
        onSelectSession(sourceName, sourceType, sessionName, cubeName, credentialInfo.is_admin_credential);
      } else if (sourceType === 'oracleepm' && strList.length === 2) {
        // Oracle EPM cube clicked
        let cubeName = strList[1];
        let credentialInfo = getCredentialStatus(sourceName);
        onSelectSession(sourceName, sourceType, '', cubeName, credentialInfo.is_admin_credential);
      } else {
        // Regular session clicked: source^session
        let sessionName = strList.length > 1 ? strList[1] : '';
        let credentialInfo = getCredentialStatus(sourceName);
        
        onSelectSession(sourceName, sourceType, sessionName, null, credentialInfo.is_admin_credential);
      }
    }
  }

  const oracleEPMSources = React.useMemo(
    () => new Set(data.filter((s) => s.type === 'oracleepm').map((s) => s.name)),
    [data]
  );

  return (
    <OracleEPMContext.Provider value={oracleEPMSources}>
      <Box sx={{ '& .MuiTreeItem-label': { fontSize: '0.8rem' } }}>
        <RichTreeView
          defaultExpandedItems={[]}
          items={treeItems}
          slots={{ item: CustomTreeItem }}
          onItemClick={handleItem}
        />
      </Box>
    </OracleEPMContext.Provider>
  );
}